/**
 * Login — Pantalla de inicio de sesión con Google OAuth
 * Solo un botón. La verificación de dominio ocurre en AuthContext.
 */
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "../../lib/icons.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { cn } from "../../lib/utils.js";
import { SoaSymbol } from "../../components/SoaMark.jsx";
import { APP_VERSION } from "../../lib/version.js";
import { PaginaCuentaAjena } from "../../components/sistema/PaginasSistema.jsx";

export default function Login() {
  const { user, loading, error, signIn, signInConPassword } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  // Ingreso con correo y contraseña (usuarios por evento que no usan Google)
  const [conPassword, setConPassword] = useState(false);
  const [correo, setCorreo] = useState("");
  const [clave, setClave] = useState("");
  async function handlePassword(e) {
    e.preventDefault();
    setSigningIn(true);
    await signInConPassword(correo, clave);
    setSigningIn(false);
  }

  // Si ya está logueado, ir directo al kanban
  if (!loading && user) {
    // Si venía de «Tu sesión venció», vuelve exactamente a donde estaba.
    let volver = null;
    try { volver = sessionStorage.getItem("crew_volver_a"); if (volver) sessionStorage.removeItem("crew_volver_a"); } catch { /* sin storage */ }
    return <Navigate to={volver && volver.startsWith("/") && !volver.startsWith("/login") ? volver : "/kanban"} replace />;
  }

  async function handleSignIn() {
    setSigningIn(true);
    await signIn();
    setSigningIn(false);
  }

  // Cuenta fuera del dominio @thecrewagencia.com → página de sistema (muestra el mail usado).
  if (error && /^Solo se permiten/.test(error)) {
    return <PaginaCuentaAjena email={(error.split("Usaste:")[1] || "").trim() || null} onOtraCuenta={handleSignIn} />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] bg-background">
      {/* Panel de marca (brand book SOA 10 PRO · pantalla de acceso): oscuro, símbolo «Estados» + nombre */}
      <aside className="relative flex flex-col justify-between bg-[#0B0D10] px-6 py-8 text-white sm:px-12 lg:px-20 lg:py-14">
        <p className="text-[17px] font-bold text-[#A6ABB6]">The Crew Agencia</p>
        <div className="py-10 lg:py-0">
          <SoaSymbol size={96} surface="transparent" className="mb-5 lg:mb-7 !h-[88px] !w-[88px] lg:!h-[142px] lg:!w-[142px]" />
          <p className="text-[56px] sm:text-[84px] lg:text-[118px] font-black leading-[0.9] tracking-[-0.04em]">
            SOA <span className="text-[#22D3EE]">10 PRO</span>
          </p>
          <p className="mt-4 text-[17px] sm:text-[22px] text-[#D5D8DF]">El sistema operativo de la agencia.</p>
        </div>
        <p className="hidden text-[14px] text-[#6B6F7A] lg:block">Asunción · thecrewagencia.com</p>
      </aside>

      <main className="flex items-center justify-center p-5 sm:p-8">
        <div className="w-full max-w-[464px] rounded-[36px] bg-card p-8 sm:p-11 shadow-[0_40px_100px_rgba(17,19,24,0.10)]" title={`Build ${APP_VERSION}`}>
          <SoaSymbol size={64} className="mb-6" />
          <h1 className="text-[30px] sm:text-[34px] leading-[1.08] font-extrabold tracking-tight text-foreground mb-3">Entrá con tu cuenta de la agencia</h1>
          <p className="text-[16px] text-muted-foreground mb-7">Con tu cuenta de la agencia o con la que te dieron para tu evento.</p>

          {error && (
            <div role="alert" className="flex items-start gap-2 px-4 py-3 rounded-2xl bg-[#FEE2E2] text-[#B91C1C] text-sm font-semibold mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={handleSignIn}
            disabled={signingIn || loading}
            className={cn(
              "w-full flex items-center justify-center gap-3 h-[56px] px-4 rounded-full",
              "bg-[#111318] text-white font-extrabold text-[16px]",
              "hover:bg-[#23262E] transition-all",
              "disabled:opacity-60 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2"
            )}
          >
            {signingIn ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="grid h-7 w-7 place-items-center rounded-full bg-white"><GoogleIcon /></span>}
            {signingIn ? "Iniciando sesión…" : "Continuar con Google"}
          </button>

          <div className="mt-4">
            {!conPassword ? (
              <button type="button" onClick={() => setConPassword(true)} className="w-full text-center text-[14px] font-bold text-[#0E7490] hover:underline">
                Tengo un usuario y contraseña
              </button>
            ) : (
              <form onSubmit={handlePassword} className="space-y-3">
                <input type="email" required autoComplete="username" value={correo} onChange={e => setCorreo(e.target.value)} placeholder="Correo"
                  className="h-[52px] w-full rounded-full border border-border bg-background px-5 text-[16px]" />
                <input type="password" required autoComplete="current-password" value={clave} onChange={e => setClave(e.target.value)} placeholder="Contraseña"
                  className="h-[52px] w-full rounded-full border border-border bg-background px-5 text-[16px]" />
                <button type="submit" disabled={signingIn || loading}
                  className="h-[52px] w-full rounded-full bg-[#0E7490] text-[16px] font-extrabold text-white disabled:opacity-60">
                  {signingIn ? "Entrando…" : "Entrar"}
                </button>
              </form>
            )}
          </div>

          <p className="mt-6 text-[14px] text-muted-foreground">
            ¿Sos cliente?{" "}
            <a href="https://clientes.thecrewagencia.com" className="font-extrabold text-[#0E7490] hover:underline">Entrá al portal</a>
          </p>
        </div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
